import { Box, Button, CircularProgress, Grid } from "@mui/material";
import * as React from "react";
import HelpedAdornedTextField from "../../../components/general/helped_adorned_text_field";
import SafetySelect from "../../../components/device/selects/safety_select";
import { useSnackbar } from "notistack";
import {
  schematicClearExportConstraint,
  schematicClearImportConstraint,
  schematicSetConstraints,
} from "../../../scripts/api_v2/schematic_client";
import useBackend from "../../../hooks/use_backend";
import { processValue } from "../../../scripts/utils";

import _ from "lodash";
import { LimitAdornment } from "../../adornments/limit_adornment";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteData } from "../../../scripts/api_v2/site_client";
import { useParams } from "react-router-dom";
import { validateValue } from "../../../scripts/utils";

export function EditNode({ node, handleClose }) {
  const queryClient = useQueryClient();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const backend = useBackend();
  const minDelay = 500;
  const [editing, setEditing] = React.useState(false);
  const [fetching, setFetching] = React.useState(false);
  const [importLimit, setImportLimit] = React.useState(node.import_constraint?.limit);
  const [exportLimit, setExportLimit] = React.useState(node.export_constraint?.limit);
  const [importSafety, setImportSafety] = React.useState(node.import_constraint?.safety_margin);
  const [exportSafety, setExportSafety] = React.useState(node.export_constraint?.safety_margin);

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  function constraintChanged(newConstraint) {
    return !_.isEqual(newConstraint, node);
  }

  function resetConstraints() {
    resetImportConstraint();
    resetExportConstraint();
  }

  function resetImportConstraint() {
    setImportLimit(node.import_constraint.limit);
    setImportSafety(node.import_constraint.safety_margin);
  }

  function resetExportConstraint() {
    setExportLimit(node.export_constraint.limit);
    setExportSafety(node.export_constraint.safety_margin);
  }

  function clearImportConstraint() {
    setImportLimit(null);
    setImportSafety(null);
  }

  function clearExportConstraint() {
    setExportLimit(null);
    setExportSafety(null);
  }

  function handleSuccessfulCommit(importc, exportc) {
    setImportLimit(importc.limit);
    setImportSafety(importc.safety_margin);
    setExportLimit(exportc.limit);
    setExportSafety(exportc.safety_margin);
    setEditing(false);
  }

  function handleDiscard() {
    resetConstraints();
    setEditing(false);
  }

  const { mutate: setConstraintsMutation } = useMutation({
    mutationFn: async ({ importc, exportc, importPrevValue, exportPrevValue, device_id }) => {
      setFetching(true);
      await schematicSetConstraints(
        backend.fetch,
        params.site_id,
        node.id,
        importc,
        exportc,
        importPrevValue,
        exportPrevValue,
        device_id,
      );
    },
    onSuccess: async (data, variables) => {
      const { importc, exportc } = variables;
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const cp = { ...prev };
        cp.devices_general.schematic = data;
        return cp;
      });
      enqueueSnackbar("Node constraints updated", { variant: "success" });
      handleSuccessfulCommit(importc, exportc);
    },
    onError: (err, variables, context) => {
      enqueueSnackbar("Setting constraint failed", { variant: "error" });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id] });
      setFetching(false);
    },
  });

  async function handleCommit() {
    const importVal = validateValue(importLimit);
    const exportVal = validateValue(exportLimit);
    const importc = importVal !== null ? { limit: importVal, safety_margin: importSafety } : null;
    const exportc = exportVal !== null ? { limit: exportVal, safety_margin: exportSafety } : null;

    if (importVal === null && exportVal === null) {
      enqueueSnackbar("No valid constraint was set, discarding changes", { variant: "warning" });
      return resetConstraints();
    } else if (importc === null && constraintChanged(importc)) {
      enqueueSnackbar("Invalid import limit, clear constraint if needed", { variant: "error" });
      return resetImportConstraint();
    } else if (exportc === null && constraintChanged(exportc)) {
      enqueueSnackbar("Invalid export limit, clear constraint if needed", { variant: "error" });
      return resetExportConstraint();
    } else if (!constraintChanged(importc) && !constraintChanged(exportc)) {
      enqueueSnackbar("No updates were made", { variant: "info" });
      return handleDiscard();
    }

    const importPrevValue = {
      limit: node.import_constraint.limit,
      safety_margin: node.import_constraint.safety_margin,
    };
    const exportPrevValue = {
      limit: node.export_constraint.limit,
      safety_margin: node.export_constraint.safety_margin,
    };
    const device_id = node.device_id;
    setConstraintsMutation({ importc, exportc, importPrevValue, exportPrevValue, device_id });
  }

  const { mutate: handleClearImExportMutation } = useMutation({
    mutationFn: async ({ isImport }) => {
      setFetching(true);
      if (isImport) {
        return await schematicClearImportConstraint(backend.fetch, params.site_id, node.id, node.device_id);
      } else {
        return await schematicClearExportConstraint(backend.fetch, params.site_id, node.id, node.device_id);
      }
    },

    onSuccess: async (data, variables) => {
      const { isImport } = variables;
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const cp = { ...prev };
        cp.devices_general.schematic = data;
        return cp;
      });
      if (isImport) {
        clearImportConstraint();
      } else {
        clearExportConstraint();
      }
      enqueueSnackbar("Node constraint cleared", { variant: "success" });
    },
    onError: (err, variables, context) => {
      enqueueSnackbar("Clearing constraint failed", { variant: "error" });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id] });
      setFetching(false);
    },
  });

  async function handleClearImport() {
    handleClearImExportMutation({ isImport: true });
  }

  async function handleClearExport() {
    handleClearImExportMutation({ isImport: false });
  }

  if (!siteData || !node) return;

  const connectionType = siteData?.config?.connection_type;

  return (
    <div display="flex">
      {fetching ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 275 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container justifyContent="start">
          <Grid md={6} xs={12} item width={"80%"} sx={{ display: "flex", justifyContent: "left" }} px={2} pt={1}>
            Export constraint
          </Grid>
          <Grid md={6} xs={12} item width={"80%"} sx={{ display: "flex", justifyContent: "left" }} px={2} pt={1}>
            Import constraint
          </Grid>
          <Grid
            md={6}
            xs={12}
            item
            width={"80%"}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            px={2}
            py={3}
          >
            <HelpedAdornedTextField
              adornment={<LimitAdornment limit={exportLimit} connectionType={connectionType} />}
              key="export"
              label={"Maximum export (W)"}
              info={"The maximum export through the node in Watts"}
              type={"text"}
              fullWidth
              value={exportLimit?.limit !== null ? exportLimit : editing ? "" : "No constraint"}
              disabled={!editing}
              onChange={(e) => setExportLimit(processValue(e.target.value, true))}
            />
          </Grid>
          <Grid
            md={6}
            xs={12}
            item
            width={"80%"}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            px={2}
            py={3}
          >
            <HelpedAdornedTextField
              adornment={<LimitAdornment limit={importLimit} connectionType={connectionType} />}
              key="import"
              label={"Maximum import (W)"}
              info={"The maximum import through the node in Watts"}
              type={"text"}
              fullWidth
              value={importLimit?.limit !== null ? importLimit : editing ? "" : "No constraint"}
              disabled={!editing}
              onChange={(e) => setImportLimit(processValue(e.target.value, true))}
            />
          </Grid>
          <Grid
            md={6}
            xs={12}
            item
            width={"80%"}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            px={2}
            py={2}
          >
            <SafetySelect
              key="export-safety"
              label={"Export safety margin"}
              fullWidth
              value={exportSafety}
              disabled={editing ? false : true}
              onChange={(e) => setExportSafety(e.target.value)}
            />
          </Grid>
          <Grid
            md={6}
            xs={12}
            item
            width={"80%"}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            px={2}
            py={2}
          >
            <SafetySelect
              key="import-safety"
              label={"Import safety margin"}
              fullWidth
              value={importSafety}
              disabled={editing ? false : true}
              onChange={(e) => setImportSafety(e.target.value)}
            />
          </Grid>
          {editing && (
            <>
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <Button color="error" onClick={() => handleClearExport()}>
                  Clear export constraint
                </Button>
              </Grid>
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <Button color="error" onClick={() => handleClearImport()}>
                  Clear import constraint
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      )}

      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: "30px",
          marginLeft: 30,
          marginRight: 30,
          marginBottom: 5,
        }}
      >
        {!editing ? (
          <>
            <Button sx={{ minWidth: 180, color: "grey" }} onClick={() => handleClose()}>
              Close
            </Button>
            <Button sx={{ minWidth: 180 }} variant="contained" onClick={() => setEditing(true)}>
              Edit constraints
            </Button>
          </>
        ) : (
          <>
            <Button sx={{ minWidth: 180, color: "grey" }} onClick={() => handleDiscard()}>
              Quit editing
            </Button>
            <Button
              sx={{ minWidth: 180 }}
              variant="contained"
              onClick={() => {
                editing ? handleCommit() : setEditing(true);
              }}
            >
              Save constraints
            </Button>
          </>
        )}
      </Box>
    </div>
  );
}
